import type { ConsumptionParams } from "./services/CabinetConsumptionApi";
import type { MyConsumptionRow, MyPlace } from "./types";

/**
 * `Ф2` (кабинет ПЗС): правила периода и сводки раздела «Потребление».
 *
 * Отдельно от компонента: границы периода, среднее в сутки, столбцы по дням и
 * параметры выгрузки проверяются без браузера. Даты — календарные строки
 * `ГГГГ-ММ-ДД` по местным часам: «день» владельца — его день, а не UTC.
 */

export type PeriodKind = "day" | "week" | "month" | "custom";

export type PeriodRange = { from: string; to: string };

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** Местная календарная дата `ГГГГ-ММ-ДД`. */
export function localIsoDate(at: Date = new Date()): string {
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

export function shiftDays(iso: string, days: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  const at = new Date(year, month - 1, day);

  at.setDate(at.getDate() + days);

  return localIsoDate(at);
}

/** Границы периода: день, неделя (7 дней), месяц (с 1-го) или свои даты. */
export function periodRangeOf(
  kind: PeriodKind,
  today: string = localIsoDate(),
  custom: PeriodRange = { from: today, to: today },
): PeriodRange {
  switch (kind) {
    case "day":
      return { from: today, to: today };
    case "week":
      return { from: shiftDays(today, -6), to: today };
    case "month": {
      const [year, month] = today.split("-");
      return { from: `${year}-${month}-01`, to: today };
    }
    case "custom":
    default:
      return { from: custom.from, to: custom.to };
  }
}

/**
 * `F.7` (`oq-ММБ-215`): период — одни сутки. За такой период столбцы не нужны:
 * один столбец ничего не показывает, а итог и показания на границах — нужны.
 */
export function isSingleDay(range: PeriodRange): boolean {
  return range.from === range.to;
}

/** Параметры экрана и файла — одни и те же. */
export function consumptionParamsOf({
  placeId,
  range,
  step = "day",
}: {
  placeId: number | null;
  range: PeriodRange;
  step?: "day" | "month";
}): ConsumptionParams {
  return {
    ...(placeId === null ? {} : { place_id: placeId }),
    from: range.from,
    to: range.to,
    step,
  };
}

/** Число календарных дней периода включительно. */
export function daysInRange(range: PeriodRange): number {
  const [fy, fm, fd] = range.from.split("-").map(Number);
  const [ty, tm, td] = range.to.split("-").map(Number);
  const from = Date.UTC(fy, fm - 1, fd);
  const to = Date.UTC(ty, tm - 1, td);

  return Math.max(1, Math.round((to - from) / 86400000) + 1);
}

/** Среднее в сутки: итог делится на число дней периода. */
export function averagePerDayOf(total: number, range: PeriodRange): number {
  return total > 0 ? total / daysInRange(range) : 0;
}

/** Сумма расхода по датам — для столбцов. */
export function dailyTotalsOf(
  rows: MyConsumptionRow[],
): Array<{ date: string; total: number }> {
  const totals = new Map<string, number>();

  for (const row of rows) {
    const value = row.consumption_kwh ?? 0;
    totals.set(row.date, (totals.get(row.date) ?? 0) + value);
  }

  return [...totals.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, total]) => ({ date, total }));
}

/** Высота столбца в процентах от максимума; ноль — столбика нет. */
export function barHeightPercent(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 0;

  return Math.max(2, Math.round((value / max) * 100));
}

/** Дата строки `ГГГГ-ММ-ДД` → `ДД.ММ.ГГГГ` без сдвига пояса. */
export function formatDay(value?: string | null): string {
  if (!value) return "—";

  const [year, month, day] = value.slice(0, 10).split("-");

  if (!year || !month || !day) return "—";

  return `${day}.${month}.${year}`;
}

/** Самая ранняя дата начала связи — для текста «данные доступны с …». */
export function linkStartOf(
  places: MyPlace[],
  placeId: number | null,
): string | null {
  const relevant =
    placeId === null
      ? places
      : places.filter((place) => place.place?.id === placeId);

  const starts = relevant
    .map((place) => place.started_at)
    .filter((value): value is string => Boolean(value))
    .sort();

  return starts[0] ?? null;
}
